import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import axios from 'axios';
import { useDDPCalculation } from '../hooks/useDDPCalculation';
import { useShipment } from '../contexts/ShipmentContext';

// Configure axios to use API_URL as base URL
axios.defaults.baseURL = API_URL

function ProductDetail({ product, onClose, category, isAuthenticated, onProductClick, allProducts, onBackToCategory,
                         currency, formatPrice, currencyLoading}) {
  const [selectedRecipe, setSelectedRecipe] = useState(product.recipes?.[0] || null);
  const { shipmentData } = useShipment();

  const [shippingCost, setShippingCost] = useState(() => {
    // Загружаем сохраненное значение из localStorage при инициализации
    const saved = localStorage.getItem('shippingCost');
    return saved || '';
  });

  // Выбираем цены для пользователя или общие цены
  const price = product.user_price || {
    exw_bulk_price: product.exw_bulk_price,
    exw_ibc_price: product.exw_ibc_price,
    cost_in_oil: product.cost_in_oil
  };

  // Рассчитываем стоимость в масле
  const costInOil = price.exw_bulk_price * product.treat_rate / 100;

  // Функция для форматирования цены с валютой
  const formatPriceWithCurrency = (priceUSD) => {
    if (!isAuthenticated) return '$$$';
    if (currencyLoading) return '...';
    return formatPrice(priceUSD);
  };

  // Функционал кнопки "скопировать ссылку на продукт"
  const [copyState, setCopyState] = useState('idle'); // 'idle', 'copied'
  const handleCopyLink = async () => {
  const productUrl = `${window.location.origin}?product=${product.id}`;

  try {
    // Проверка безопасности для продакшена
    if (!navigator.clipboard || !window.isSecureContext) {
      // Fallback для HTTP
      const textArea = document.createElement('textarea');
      textArea.value = productUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
    } else {
      // HTTPS - используем современный API
      await navigator.clipboard.writeText(productUrl);
    }

    setCopyState('copied');
    setTimeout(() => setCopyState('idle'), 1500);

  } catch (err) {
    console.error('Ошибка копирования:', err);
    // Если совсем ничего не работает
    alert('Ссылка на продукт: ' + productUrl);
  }
};

  // Добавляем useEffect для обновления выбранного рецепта при смене продукта
  useEffect(() => {
    setSelectedRecipe(product.recipes?.[0] || null);
  }, [product]); // Зависимость от product - сработает при каждом изменении продукта

  const getSpecColor = (spec) => {
    if (spec.spec_type === 'api' || spec.name.includes('API')) {
      return { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' };
    } else if (spec.spec_type === 'acea' || spec.name.includes('ACEA')) {
      return { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' };
    } else if (spec.name.includes('MB ') || spec.name.startsWith('MB')) {
      return { bg: '#FFFBEB', color: '#A16207', border: '#FDE68A' };
    } else if (spec.spec_type === 'oem') {
      return { bg: '#F0FDF4', color: '#166534', border: '#BBF7D0' };
    } else {
      return { bg: '#F3F4F6', color: '#4B5563', border: '#E5E7EB' };
    }
  };

  const handleMaterialClick = async (material) => {
    if (onProductClick) {
      // Сначала ищем в локальном массиве
      let fullProduct = allProducts.find(
        p => p.code === material.component
      );

      if (fullProduct) {
        // Если нашли локально - используем его
        onProductClick(fullProduct);
      } else {
        // Если не нашли - делаем запрос к API
        try {
          const response = await axios.get(`/api/products/?code=${material.component}`);
          const apiProduct = Array.isArray(response.data)
            ? response.data[0]
            : response.data.results?.[0] || response.data;

          if (apiProduct) {
            onProductClick(apiProduct);
          } else {
            console.error("❌ Продукт не найден ни локально, ни через API:", material.component);
          }
        } catch (error) {
          console.error('❌ Ошибка при загрузке продукта через API:', error);
        }
      }
    }
  };

  // Функция для подсчета общей стоимости всех материалов
    const calculateTotalCost = (materials) => {
      if (!materials?.length) return 0;

      return materials.reduce((total, material) => {
        // Определяем цену по логике: авторизован → price или tempPrice, иначе → tempPrice
        const price = isAuthenticated
          ? (material.price ?? material.tempPrice)
          : material.tempPrice;

        if (!price || !material.percentage) return total;

        const percentage = parseFloat(material.percentage.toString().replace('%', '')) || 0;
        const priceValue = parseFloat(price) || 0;

        return total + (percentage / 100) * priceValue;
      }, 0);
    };

  // Рассчитываем DDP в USD
  const ddpData = useDDPCalculation(price.exw_bulk_price || price.exw_ibc_price, shipmentData);

  // Форматируем результаты
  const formattedResults = useMemo(() => {
    if (!ddpData) return null;

    return {
      totalDDP: ddpData.totalDDP,
      perGrossTon: ddpData.perGrossTon,
      perNetTon: ddpData.perNetTon,
      perGrossKg: ddpData.perGrossKg,
      perNetKg: ddpData.perNetKg,
      cifValue: ddpData.cifValue,
      customsDuty: ddpData.customsDuty,
      vat: ddpData.vat
    };
  }, [ddpData]);

  console.log('Shipment data from context:', shipmentData);
  console.log('DDP results:', ddpData);

  return (
    <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
      {/* Кнопка "Назад" */}
      <button
          onClick={() => {
            onBackToCategory();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: '#374151',
            border: 'none',
            padding: '10px 18px 10px 14px',
            borderRadius: '40px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            letterSpacing: '0.3px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1F2937';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#374151';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)';
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Back</span>
        </button>

        {/* Кнопка "Скопировать ссылку" */}
        <button
          onClick={handleCopyLink}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            padding: '8px 16px',
            borderRadius: '6px',
            color: copyState === 'copied' ? '#059669' : '#1F2937', // зеленый когда скопировано
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (copyState !== 'copied') {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
              e.currentTarget.style.borderColor = '#D1D5DB';
            }
          }}
          onMouseLeave={(e) => {
            if (copyState !== 'copied') {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transition: 'transform 0.2s ease'
            }}
          >
            {copyState === 'copied' ? (
              // Галочка когда скопировано
              <polyline points="20 6 9 17 4 12" />
            ) : (
              // Цепочка
              <>
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </>
            )}
          </svg>
          <span>{copyState === 'copied' ? 'Copied!' : 'Copy link'}</span>
        </button>
      </div>

      <div className="D2">
      {/* Left Column - Card */}
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '10px 14px',
          borderBottom: `3px solid ${product.color}`,
          background: '#F9FAFB'
        }}>
          <div style={{
            fontSize: '9px',
            fontWeight: '700',
            color: product.color,
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
            Product Information
          </div>
          <div style={{ fontSize: '13px', fontWeight: '800', color: '#111827', marginTop: '2px' }}>
            {product.code} — {product.name}
          </div>
        </div>

        {/* Full Description Section */}
        {product.full_description && (
          <div style={{
            padding: '12px 14px',
            borderBottom: '1px solid #E5E7EB',
            background: '#FFFFFF'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#6B7280',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Description
            </div>
            <div style={{
              fontSize: '12px',
              lineHeight: '1.5',
              color: '#374151'
            }}>
              {product.full_description}
            </div>
          </div>
        )}

        <div style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#6B7280',
              padding: '12px 14px',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Typical Properties
            </div>
          
          {/* Typical Properties Table */}
          <div className="TR">
            <span style={{ fontWeight: '600', color: '#374151' }}>Appearance</span>
            <span style={{ color: '#9CA3AF', textAlign: 'center' }}>Visual</span>
            <span style={{ color: '#9CA3AF', textAlign: 'center' }}>—</span>
            <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
              {product.appearance}
            </span>
          </div>
          
          {product.kv_40 && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>KV @40°C</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 445</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>mm²/s</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.kv_40}
              </span>
            </div>
          )}
          
          {product.kv_100 && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>KV @100°C</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 445</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>mm²/s</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.kv_100}
              </span>
            </div>
          )}
          
          {product.viscosity_index && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Viscosity Index</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 2270</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>—</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.viscosity_index}
              </span>
            </div>
          )}
          
          {product.ccs_minus_20 && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>CCS @-20°C</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 5293</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>mPa·s</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.ccs_minus_20}
              </span>
            </div>
          )}
          
          {product.density && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Density @15°C</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 1298</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>kg/m³</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.density}
              </span>
            </div>
          )}
          
          {product.flash_point && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Flash Point</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 92</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>°C</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.flash_point}
              </span>
            </div>
          )}
          
          {product.pour_point && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Pour Point</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 97</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>°C</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.pour_point}
              </span>
            </div>
          )}
          
          {product.tbn_typical && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>TBN</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 2896</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>mgKOH/g</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.tbn_typical}
              </span>
            </div>
          )}
          
          {product.sulfated_ash && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Sulfated Ash</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 874</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.sulfated_ash}
              </span>
            </div>
          )}
          
          {product.nitrogen && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Nitrogen</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.nitrogen}
              </span>
            </div>
          )}
          
          {product.calcium && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Calcium</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.calcium}
              </span>
            </div>
          )}
          
          {product.phosphorus && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Phosphorus</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.phosphorus}
              </span>
            </div>
          )}
          
          {product.zinc && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Zinc</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.zinc}
              </span>
            </div>
          )}
          
          {product.magnesium && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Magnesium</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.magnesium}
              </span>
            </div>
          )}
          
          {product.boron && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Boron</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.boron}
              </span>
            </div>
          )}
          
          {product.molybdenum && (
            <div className="TR">
              <span style={{ fontWeight: '600', color: '#374151' }}>Molybdenum</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>D 4951</span>
              <span style={{ color: '#9CA3AF', textAlign: 'center' }}>% mass</span>
              <span style={{ fontWeight: '700', color: '#111827', textAlign: 'right', fontFamily: 'monospace' }}>
                {product.molybdenum}
              </span>
            </div>
          )}

          {/* Treat Rate Block - Multiple Treat Rates */}
          {(product.treat_rates && product.treat_rates.length > 0) || product.treat_rate ? (
            <div style={{ 
              padding: '7px 14px', 
              background: `${product.color}06`, 
              borderTop: `1px solid ${product.color}15`
            }}>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '700', 
                color: product.color, 
                letterSpacing: '.8px', 
                textTransform: 'uppercase', 
                marginBottom: '3px' 
              }}>
                Treat Rate{product.treat_rates && product.treat_rates.length > 1 ? '(s)' : ''}
              </div>
              
              {/* New treat_rates array */}
              {product.treat_rates && product.treat_rates.length > 0 ? (
                product.treat_rates.map((treatRate, index) => (
                  <div key={treatRate.id || index}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '10px', 
                      marginBottom: '2px' 
                    }}>
                      <span style={{ color: '#374151' }}>{treatRate.specification}</span>
                      <span style={{ fontWeight: '800', color: product.color, fontFamily: 'monospace' }}>
                        {treatRate.rate}%
                      </span>
                    </div>
                    {treatRate.tbn_in_oil && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        fontSize: '9px', 
                        marginBottom: '4px',
                        paddingLeft: '8px'
                      }}>
                        <span style={{ color: '#92400E', fontWeight: '600' }}>→ TBN in oil: {treatRate.tbn_in_oil} mgKOH/g</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                /* Fallback to old single treat_rate field */
                <>
                  {product.treat_rate && product.api_spec && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '10px', 
                      marginBottom: '2px' 
                    }}>
                      <span style={{ color: '#374151' }}>{product.api_spec}</span>
                      <span style={{ fontWeight: '800', color: product.color, fontFamily: 'monospace' }}>
                        {product.treat_rate}%
                      </span>
                    </div>
                  )}
                  {product.tbn_in_oil && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontSize: '10px', 
                      marginBottom: '2px' 
                    }}>
                      <span style={{ color: '#92400E', fontWeight: '600' }}>→ TBN in finished oil</span>
                      <span style={{ fontWeight: '800', color: '#92400E', fontFamily: 'monospace' }}>
                        {product.tbn_in_oil} mgKOH/g
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}

          {/* Pricing Block */}
          {(price.exw_bulk_price || price.exw_ibc_price) && (
            <div style={{ 
              padding: '9px 14px', 
              background: '#EFF6FF', 
              borderTop: '1px solid #BFDBFE' 
            }}>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '700', 
                color: '#1D4ED8', 
                letterSpacing: '.8px', 
                textTransform: 'uppercase', 
                marginBottom: '5px' 
              }}>
                Pricing (USD/MT) EXW Dubai
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                {price.exw_bulk_price && (
                  <div style={{ 
                    padding: '7px', 
                    background: '#fff', 
                    borderRadius: '4px', 
                    border: '1px solid #BFDBFE' 
                  }}>
                    <div style={{ fontSize: '7px', color: '#6B7280', fontWeight: '600' }}>BULK</div>
                    <div style={{ fontSize: '15px', fontWeight: '800', color: '#1D4ED8', fontFamily: 'monospace' }}>
                      {formatPriceWithCurrency(price.exw_bulk_price)}
                    </div>
                  </div>
                )}
                {price.exw_ibc_price && (
                  <div style={{ 
                    padding: '7px', 
                    background: '#fff', 
                    borderRadius: '4px', 
                    border: '1px solid #BFDBFE' 
                  }}>
                    <div style={{ fontSize: '7px', color: '#6B7280', fontWeight: '600' }}>IBC/DRUMS</div>
                    <div style={{ fontSize: '15px', fontWeight: '800', color: '#1D4ED8', fontFamily: 'monospace' }}>
                      {formatPriceWithCurrency(price.exw_ibc_price)}
                    </div>
                  </div>
                )}
              </div>
              {costInOil != null && (
                <div style={{ 
                  marginTop: '5px', 
                  padding: '7px', 
                  background: '#F0FDF4', 
                  borderRadius: '4px', 
                  border: '1px solid #BBF7D0' 
                }}>
                  <div style={{ fontSize: '7px', color: '#166534', fontWeight: '600' }}>
                    COST IN FINISHED OIL
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '800', color: '#166534', fontFamily: 'monospace' }}>
                    {isAuthenticated ? formatPriceWithCurrency(costInOil) + '/MT' : '$$$/MT'}
                  </div>
                </div>
              )}
              <div style={{ 
                fontSize: '7px', 
                color: '#6B7280', 
                marginTop: '5px', 
                fontStyle: 'italic' 
              }}>
                EXW Jebel Ali. Subject to volume and contract terms.
              </div>
            </div>
          )}

          {/* DDP CALCULATION BLOCK */}
            {isAuthenticated && (
              <div style={{
                padding: '9px 14px',
                background: '#FEF3C7',
                borderTop: '1px solid #FDE68A'
              }}>
                <div style={{
                  fontSize: '9px',
                  fontWeight: '700',
                  color: '#B45309',
                  letterSpacing: '.8px',
                  textTransform: 'uppercase',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  🚢 DDP Delivered Price
                  <span style={{ fontSize: '7px', fontWeight: '400', color: '#92400E' }}>
                    (Based on current shipment config)
                  </span>
                </div>

                {/* DDP Table */}
                <div style={{
                  background: '#fff',
                  borderRadius: '6px',
                  border: '1px solid #FDE68A',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #FDE68A' }}>
                        <td style={{ padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                          DDP PER GROSS TON
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '14px', fontWeight: '800', color: '#B45309', fontFamily: 'monospace' }}>
                          {ddpData ? `${formatPrice(ddpData.perGrossTon)}` : '—'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #FDE68A' }}>
                        <td style={{ padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                          DDP PER NET TON
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '14px', fontWeight: '800', color: '#B45309', fontFamily: 'monospace' }}>
                          {ddpData ? `${formatPrice(ddpData.perNetTon)}` : '—'}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #FDE68A' }}>
                        <td style={{ padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                          DDP PER GROSS KG
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '14px', fontWeight: '800', color: '#B45309', fontFamily: 'monospace' }}>
                          {ddpData ? `${formatPrice(ddpData.perGrossKg)}` : '—'}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px 10px', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                          DDP PER NET KG
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '14px', fontWeight: '800', color: '#B45309', fontFamily: 'monospace' }}>
                          {ddpData ? `${formatPrice(ddpData.perNetKg)}` : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Info Line */}
                <div style={{
                  fontSize: '7px',
                  color: '#92400E',
                  marginTop: '8px',
                  fontStyle: 'italic',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>DDP: Delivered Duty Paid (door to door)</span>
                  {ddpData && (
                    <span>EXW: ${formatPrice(price.exw_bulk_price || price.exw_ibc_price)}/MT → DDP: {formatPrice(ddpData.perGrossTon)}/MT</span>
                  )}
                </div>
                {!ddpData && (
                  <div style={{
                    fontSize: '9px',
                    color: '#92400E',
                    marginTop: '6px',
                    textAlign: 'center',
                    background: '#FFF7ED',
                    padding: '6px',
                    borderRadius: '4px'
                  }}>
                    ⚠️ No shipment data configured. Click "Shipment Data" in header to configure.
                  </div>
                )}
              </div>
            )}

          {/* Download TDS Button */}
          <div style={{ 
            padding: '9px 14px', 
            background: '#F8FAFC', 
            borderTop: '1px solid #E5E7EB' 
          }}>
            <button 
              className="dlb"
              onClick={() => {
                window.open(`${API_URL}/api/products/${product.id}/download_tds/`, '_blank');
              }}
            >
              Download TDS (PDF)
            </button>
          </div>
        </div>

        {/* Right Column - Specifications, Viscosity Grades, Key Advantage, Applications */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Specifications & Approvals */}
          {product.specifications && product.specifications.length > 0 && (
            <div style={{ 
              background: '#fff', 
              borderRadius: '8px', 
              border: '1px solid #E5E7EB', 
              padding: '12px'
            }}>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '700', 
                color: '#6B7280', 
                letterSpacing: '1px', 
                textTransform: 'uppercase', 
                marginBottom: '6px' 
              }}>
                Specifications & Approvals
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                {product.specifications.map((spec) => {
                  const style = getSpecColor(spec);
                  return (
                    <span
                      key={spec.id}
                      style={{
                        display: 'inline-block',
                        padding: '2px 7px',
                        borderRadius: '4px',
                        fontSize: '9px',
                        fontWeight: '600',
                        background: style.bg,
                        color: style.color,
                        border: `1px solid ${style.border}`,
                        margin: '0 3px 3px 0'
                      }}
                    >
                      {spec.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Viscosity Grades */}
          {product.viscosity_grades && product.viscosity_grades.length > 0 && (
            <div style={{ 
              background: '#fff', 
              borderRadius: '8px', 
              border: '1px solid #E5E7EB', 
              padding: '12px'
            }}>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '700', 
                color: '#6B7280', 
                letterSpacing: '1px', 
                textTransform: 'uppercase', 
                marginBottom: '5px' 
              }}>
                Viscosity Grades
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                {product.viscosity_grades.map((grade) => (
                  <span
                    key={grade.id}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '5px',
                      fontSize: '10px',
                      fontWeight: '700',
                      background: `${product.color}08`,
                      color: product.color,
                      border: `1px solid ${product.color}18`
                    }}
                  >
                    {grade.grade}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Key Advantage */}
          {product.key_advantage && (
            <div style={{ 
              background: `${product.color}04`, 
              borderRadius: '8px', 
              border: `1px solid ${product.color}12`, 
              padding: '12px',
              borderLeft: `3px solid ${product.color}`
            }}>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '700', 
                color: product.color, 
                letterSpacing: '1px', 
                textTransform: 'uppercase', 
                marginBottom: '4px' 
              }}>
                Key Advantage
              </div>
              <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                {product.key_advantage}
              </div>
            </div>
          )}

          {/* Features & Benefits */}
          {product.features_benefits && (
            <div style={{
              background: '#fff',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              padding: '12px'
            }}>
              <div style={{
                fontSize: '9px',
                fontWeight: '700',
                color: '#6B7280',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: '5px'
              }}>
                Features & Benefits
              </div>
              <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                {product.features_benefits}
              </div>
            </div>
          )}

          {/* General application */}
          {product.general_application && (
            <div style={{
              background: `${product.color}04`,
              borderRadius: '8px',
              border: `1px solid ${product.color}12`,
              padding: '12px',
              borderLeft: `3px solid ${product.color}`
            }}>
              <div style={{
                fontSize: '9px',
                fontWeight: '700',
                color: product.color,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>
                General application
              </div>
              <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                {product.general_application}
              </div>
            </div>
          )}

          {/* Applications */}
          {product.applications && product.applications.length > 0 && (
            <div style={{ 
              background: '#fff', 
              borderRadius: '8px', 
              border: '1px solid #E5E7EB', 
              padding: '12px'
            }}>
              <div style={{ 
                fontSize: '9px', 
                fontWeight: '700', 
                color: '#6B7280', 
                letterSpacing: '1px', 
                textTransform: 'uppercase', 
                marginBottom: '5px' 
              }}>
                Applications
              </div>
              {product.applications.map((app) => (
                <div
                  key={app.id}
                  style={{
                    display: 'flex',
                    gap: '4px',
                    marginBottom: '2px',
                    fontSize: '10px',
                    color: '#4B5563',
                    lineHeight: '1.3',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  <span style={{ color: product.color, fontWeight: '700' }}>›</span>
                  <span>{app.description}</span>
                </div>
              ))}
            </div>
          )}

          {/* Storage & Use */}
          {product.storage_and_use && (
            <div style={{
              background: '#fff',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              padding: '12px'
            }}>
              <div style={{
                fontSize: '9px',
                fontWeight: '700',
                color: '#6B7280',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                marginBottom: '5px'
              }}>
                Storage & Use
              </div>
              <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                {product.storage_and_use}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Formulations Section - Full Width */}
      {product.recipes && product.recipes.length > 0 && (
        <div style={{ gridColumn: '1/-1', marginTop: '8px' }}>
          <div style={{ 
            background: '#fff', 
            borderRadius: '8px', 
            border: '1px solid #E5E7EB', 
            overflow: 'hidden' 
          }}>
            <div style={{ 
              padding: '10px 14px', 
              background: `${product.color}08`, 
              borderBottom: `2px solid ${product.color}` 
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                flexWrap: 'wrap', 
                gap: '6px' 
              }}>
                <div>
                  <div style={{ 
                    fontSize: '9px', 
                    fontWeight: '700', 
                    color: product.color, 
                    letterSpacing: '1px', 
                    textTransform: 'uppercase' 
                  }}>
                    Formulations
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#111827', marginTop: '2px' }}>
                    {product.code} — Finished Oil Recipes
                  </div>
                </div>

              </div>
            </div>

            {selectedRecipe && (
              <div>
                <div style={{ padding: '10px 14px', background: '#FAFBFC', borderBottom: '1px solid #E5E7EB' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between', // Распределяем пространство между элементами
                    alignItems: 'flex-start', // Выравниваем по центру вертикально
                    width: '100%'
                  }}>
                    {/* Первый блок - SAE grade и API spec - прижат влево */}
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#111827' }}>
                      {selectedRecipe.sae_grade} — {selectedRecipe.api_spec_recipe}
                    </div>

                  {/* Второй блок - селект - прижат вправо */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}>
                      <select
                    value={selectedRecipe?.id || ''}
                    onChange={(e) => {
                      const recipeId = parseInt(e.target.value);
                      const recipe = product.recipes.find(r => r.id === recipeId);
                      setSelectedRecipe(recipe);
                    }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: `1px solid ${product.color}`,
                      fontSize: '13px',
                      fontWeight: '500',
                      color: product.color,
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none',
                      width: '200px'
                    }}
                  >
                    {product.recipes.map((recipe) => {
                      // Находим материал в рецепте, где component === product.code
                      const material = recipe.raw_materials?.find(
                        rm => rm.component === product.code
                      );

                      // Получаем процент и умножаем на цену продукта
                      // Формируем отображение цены только для авторизованных пользователей
                      let displayPrice = '';
                      if (isAuthenticated) {
                        if (material?.percentage && price.exw_ibc_price) {
                          const calculatedPrice = (parseFloat(price.exw_ibc_price) * parseFloat(material.percentage) / 100).toFixed(2);
                          displayPrice = ` - $${calculatedPrice}`;
                        } else if (price.exw_ibc_price) {
                          displayPrice = ` - $${price.exw_ibc_price}`;
                        }
                      }

                      return (
                        <option
                          key={recipe.id}
                          value={recipe.id}
                        >
                          {recipe.sae_grade} {displayPrice}
                        </option>
                      );
                    })}
                  </select>
                </div>
                </div>
                  <div style={{ fontSize: '8px', color: '#9CA3AF' }}>
                    Formulation Code: {selectedRecipe.formulation_code}
                  </div>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                  gap: '0' 
                }}>
                  <div style={{ borderRight: '1px solid #E5E7EB' }}>
                  <div style={{
                    padding: '8px 14px',
                    background: '#F3F4F6',
                    borderBottom: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      fontSize: '9px',
                      fontWeight: '700',
                      color: '#374151',
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase'
                    }}>
                      Raw Materials
                    </div>
                  </div>
                   {/* Header with Price column */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(120px, 1fr) 80px 70px 90px',
                    padding: '4px 14px',
                    fontSize: '7px',
                    fontWeight: '700',
                    color: '#9CA3AF',
                    borderBottom: '1px solid #E5E7EB'
                  }}>
                    <span>Component</span>
                    <span style={{ textAlign: 'center' }}>Type</span>
                    <span style={{ textAlign: 'right' }}>% mass</span>
                    <span style={{ textAlign: 'right' }}>Price/1000kg ($)</span>
                  </div>

                  {/* Raw Materials Rows */}
                  {selectedRecipe.raw_materials?.map((material, idx) => (
                    <div
                      key={material.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(120px, 1fr) 80px 70px 90px',
                        padding: '5px 14px',
                        background: idx % 2 === 0 ? '#F9FAFB' : '#fff',
                        borderBottom: '1px solid #F3F4F6',
                        fontSize: '9px',
                        alignItems: 'center'
                      }}
                    >
                     {/* Если есть цена - делаем кликабельным */}
                    {material.price ? (
                      <div
                        onClick={() => handleMaterialClick(material)}
                        style={{
                          fontWeight: material.component === product.code ? '800' : '600',
                          color: '#2563EB',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {material.component}
                      </div>
                    ) : (
                      <span style={{
                        fontWeight: material.component === product.code ? '800' : '600',
                        color: '#374151',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {material.component}
                      </span>
                    )}
                      <span style={{
                        color: '#6B7280',
                        textAlign: 'center',
                        fontSize: '8px'
                      }}>
                        {material.material_type}
                      </span>
                      <span style={{
                        fontWeight: '700',
                        color: '#111827',
                        textAlign: 'right',
                        fontFamily: 'monospace'
                      }}>
                        {material.percentage}
                      </span>

                      {/* Price cell */}
                        <div style={{ textAlign: 'right' }}>
                          {isAuthenticated && material.price ? (
                            // Авторизован и цена есть - показываем цену
                            <span style={{
                              fontWeight: '600',
                              color: '#059669',
                              fontFamily: 'monospace'
                            }}>
                              ${parseFloat(material.price).toFixed(2)}
                            </span>
                          ) : (
                            // Во всех остальных случаях - поле ввода
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={material.tempPrice || ''}
                              onChange={(e) => {
                                const newPrice = e.target.value;
                                const updatedMaterials = [...selectedRecipe.raw_materials];
                                updatedMaterials[idx] = {
                                  ...material,
                                  tempPrice: newPrice
                                };
                                setSelectedRecipe({
                                  ...selectedRecipe,
                                  raw_materials: updatedMaterials
                                });
                              }}
                              style={{
                                width: '70px',
                                padding: '2px 4px',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                fontSize: '9px',
                                textAlign: 'right',
                                fontFamily: 'monospace'
                              }}
                            />
                          )}
                        </div>
                    </div>
                  ))}

                  {/* Shipping Cost Row */}
                    {selectedRecipe.raw_materials && selectedRecipe.raw_materials.length > 0 && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto 70px',
                        padding: '8px 14px',
                        background: '#F9FAFB',
                        borderTop: '1px solid #E5E7EB',
                        borderBottom: '1px solid #E5E7EB',
                        fontSize: '10px',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          color: '#374151',
                          fontWeight: '600'
                        }}>
                          Shipping Cost
                        </span>
                        <span></span> {/* Empty cell for Type */}
                        <span></span> {/* Empty cell for % mass */}
                        <div style={{ textAlign: 'right' }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={shippingCost || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setShippingCost(value);
                              // Сохраняем в localStorage
                              localStorage.setItem('shippingCost', value);
                            }}
                            style={{
                              width: '70px',
                              padding: '2px 4px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '4px',
                              fontSize: '9px',
                              textAlign: 'right',
                              fontFamily: 'monospace'
                            }}
                          />
                        </div>
                      </div>
                    )}

                  {/* Total Cost Row */}
                  {selectedRecipe.raw_materials && selectedRecipe.raw_materials.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto 70px',
                      padding: '8px 14px',
                      background: '#F3F4F6',
                      borderTop: '2px solid #E5E7EB',
                      fontSize: '10px',
                      fontWeight: '700'
                    }}>
                      <span style={{ color: '#374151' }}>Total Cost</span>
                      <span></span> {/* Empty cell for Type */}
                      <span></span> {/* Empty cell for % mass */}
                      <span style={{
                        textAlign: 'right',
                        color: '#059669',
                        fontFamily: 'monospace',
                        paddingLeft: '8px'
                      }}>
                        ${(calculateTotalCost(selectedRecipe.raw_materials) + (parseFloat(shippingCost) || 0)).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                  <div>
                    <div style={{ 
                      padding: '8px 14px', 
                      background: '#F3F4F6', 
                      borderBottom: '1px solid #E5E7EB' 
                    }}>
                      <div style={{ 
                        fontSize: '9px', 
                        fontWeight: '700', 
                        color: '#374151', 
                        letterSpacing: '0.8px', 
                        textTransform: 'uppercase' 
                      }}>
                        Test Results (Finished Oil)
                      </div>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr auto auto auto', 
                      padding: '4px 14px', 
                      fontSize: '7px', 
                      fontWeight: '700', 
                      color: '#9CA3AF', 
                      borderBottom: '1px solid #E5E7EB' 
                    }}>
                      <span>Property</span>
                      <span style={{ textAlign: 'center', padding: '0 6px' }}>Method</span>
                      <span style={{ textAlign: 'center', padding: '0 6px' }}>Unit</span>
                      <span style={{ textAlign: 'right' }}>Value</span>
                    </div>
                    {selectedRecipe.test_results?.map((result, idx) => (
                      <div
                        key={result.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto auto',
                          padding: '4px 14px',
                          background: idx % 2 === 0 ? '#F9FAFB' : '#fff',
                          borderBottom: '1px solid #F3F4F6',
                          fontSize: '9px'
                        }}
                      >
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {result.property_name}
                        </span>
                        <span style={{ 
                          color: '#9CA3AF', 
                          textAlign: 'center', 
                          padding: '0 6px', 
                          fontSize: '8px' 
                        }}>
                          {result.method}
                        </span>
                        <span style={{ 
                          color: '#9CA3AF', 
                          textAlign: 'center', 
                          padding: '0 6px', 
                          fontSize: '8px' 
                        }}>
                          {result.unit}
                        </span>
                        <span style={{ 
                          fontWeight: '700', 
                          color: '#111827', 
                          textAlign: 'right', 
                          fontFamily: 'monospace' 
                        }}>
                          {result.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ 
                  padding: '10px 14px', 
                  background: '#F8FAFC', 
                  borderTop: '1px solid #E5E7EB' 
                }}>
                  <button 
                    className="dlb"
                    style={{ background: '#C62828' }}
                    onClick={() => {
                      console.log('product_id: ', {product})
                      window.open(`${API_URL}/api/recipes/${selectedRecipe.id}/export_pdf/?product=${product.id}`, '_blank');
                    }}
                  >
                    ⬇ Export PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;
